/**
 * Branding (DISENO §12; docs/activa/FASE-3-PLAN.md, «Branding»). Del equipo, bajo «Tu marca». El logo, la web, el color,
 * la letra, cómo habla la marca, las palabras que no usa y la firma, con la vista previa en vivo de un mail, un WhatsApp
 * y la portada del reporte (con los números de la última campaña).
 *
 * Es la única voz de la marca: desde que se guarda, Reactivación arranca cada campaña con este tono, esta firma y estas
 * palabras. La primera vez se carga con la voz que ya usaba Reactivación, para no escribirla dos veces.
 */

import { useRef, useState, type ChangeEvent, type FormEvent, type KeyboardEvent } from "react";
import { Check, ImagePlus, Loader2, Trash2, X } from "lucide-react";
import { usePedido } from "../../emails/hooks/usePedido";
import { llamar } from "../../wa-oficial/components/base/comun";
import { ErrorTarjeta, Esqueleto } from "../../wa-oficial/components/estadisticas/comun";
import { textoError } from "../../wa-oficial/logic/respuesta";
import { Opciones } from "../../segmentaciones/components/Opciones";
import { btnPrimario, btnSecundario } from "../../segmentaciones/components/PasosCampana";
import { EncabezadoPagina, Pagina } from "../../../components/activa/Pagina";
import { diaDe } from "../../descuentos/logic/descuentos";
import { brandingApi } from "../api";
import { VistaMail, VistaReporte, VistaWhatsApp, type EstiloDeMarca } from "../components/Vistas";
import {
  CODIGO_DE_EJEMPLO,
  COLORES,
  FIRMA_MAX,
  LETRAS,
  PALABRA_MAX,
  TONOS,
  esColor,
  formularioDe,
  kb,
  leerLogo,
  nombreParaMostrar,
  palabrasEn,
  problemaDeLaPalabra,
  textoSobre,
  tonoDe,
  validarFormulario,
  webParaMostrar,
  type Campo,
  type Formulario,
} from "../logic/branding";
import type { Branding, Letra, TonoMarca } from "../types";

const campoClase = (conError: boolean) =>
  `min-h-[44px] w-full rounded-xl border bg-white px-3 text-sm text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${conError ? "border-red-400" : "border-slate-200"}`;
const ayuda = "text-sm text-slate-600";
const seccion = "space-y-2 border-t border-slate-100 py-5 first:border-t-0";
const CAMPOS: Campo[] = ["logo", "web", "color", "palabras", "firma"];

function TextoDeError({ id, texto }: { id?: string; texto?: string }) {
  if (!texto) return null;
  return (
    <p id={id} role="alert" className="text-sm font-medium text-red-700">
      {texto}
    </p>
  );
}

function LogoDeLaMarca({ estilo, bytes, error, onCambio }: { estilo: EstiloDeMarca; bytes: number | null; error?: string; onCambio: (logo: string | null) => void }) {
  const archivo = useRef<HTMLInputElement>(null);
  const [problema, setProblema] = useState("");
  const [leyendo, setLeyendo] = useState(false);
  const elegir = async (e: ChangeEvent<HTMLInputElement>) => {
    const a = e.target.files?.[0];
    // así elegir el mismo archivo otra vez también avisa
    e.target.value = "";
    if (!a) return;
    setLeyendo(true);
    const r = await leerLogo(a);
    setLeyendo(false);
    if ("problema" in r) return setProblema(r.problema);
    setProblema("");
    onCambio(r.dataUrl);
  };
  const nombre = nombreParaMostrar(estilo.marca);
  return (
    <div className={seccion}>
      <p className="text-sm font-semibold text-slate-900">Logo</p>
      <div className="flex flex-wrap items-center gap-3.5">
        <div
          className={`flex h-14 min-w-[120px] max-w-[200px] items-center justify-center rounded-xl bg-white px-3 ${estilo.logo ? "border border-slate-200" : "border border-dashed border-slate-300"}`}
          data-testid="logo-actual"
        >
          {estilo.logo ? (
            <img src={estilo.logo} alt={`Logo de ${nombre}`} className="h-11 max-w-full object-contain" />
          ) : (
            <span className="text-sm text-slate-500">Sin logo todavía</span>
          )}
        </div>
        <input
          ref={archivo}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={elegir}
          aria-label="Elegir el logo"
          aria-describedby="logo-ayuda"
          className="sr-only"
          tabIndex={-1}
        />
        <button type="button" className={btnSecundario} onClick={() => archivo.current?.click()} disabled={leyendo}>
          {leyendo ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <ImagePlus size={16} aria-hidden="true" />}
          {estilo.logo ? "Cambiar logo" : "Subir logo"}
        </button>
        {estilo.logo && (
          <button
            type="button"
            className={btnSecundario}
            onClick={() => {
              setProblema("");
              onCambio(null);
            }}
          >
            <Trash2 size={16} aria-hidden="true" />
            Sacar logo
          </button>
        )}
      </div>
      <p id="logo-ayuda" className={ayuda}>
        PNG, JPG o WebP, hasta 300 KB{estilo.logo && bytes ? `. El de ahora pesa ${kb(bytes)}` : ""}.
      </p>
      <TextoDeError texto={problema || error} />
    </div>
  );
}

function ColorDeLaMarca({ color, error, onCambio }: { color: string; error?: string; onCambio: (c: string) => void }) {
  const claro = esColor(color) && textoSobre(color) !== "#FFFFFF";
  return (
    <div className={seccion}>
      <fieldset className="space-y-2" aria-describedby="color-ayuda" aria-invalid={!!error || undefined}>
        <legend className="text-sm font-semibold text-slate-900">Color de la marca</legend>
        <div className="flex flex-wrap items-center gap-3">
          {COLORES.map((c) => {
            const elegido = color.toUpperCase() === c.hex;
            return (
              <label
                key={c.hex}
                className={`inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full focus-within:ring-2 focus-within:ring-violet-400 focus-within:ring-offset-2 ${elegido ? "ring-2 ring-violet-600 ring-offset-2" : ""}`}
                style={{ background: c.hex }}
              >
                <input type="radio" name="color" value={c.hex} checked={elegido} onChange={() => onCambio(c.hex)} aria-label={`${c.nombre} (${c.hex})`} className="sr-only" />
                {elegido && <Check size={18} color="#FFFFFF" aria-hidden="true" />}
              </label>
            );
          })}
          <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-800 focus-within:ring-2 focus-within:ring-violet-400">
            <input
              type="color"
              value={esColor(color) ? color.toLowerCase() : "#111114"}
              onChange={(e) => onCambio(e.target.value.toUpperCase())}
              aria-label="Otro color"
              className="h-7 w-7 cursor-pointer rounded border-0 bg-transparent p-0"
            />
            Otro
          </label>
          <label className="inline-flex items-center">
            <span className="sr-only">Código del color</span>
            <input
              value={color}
              maxLength={7}
              onChange={(e) => onCambio(e.target.value.trim())}
              aria-invalid={!!error || undefined}
              aria-describedby={error ? "color-error" : undefined}
              className={`${campoClase(!!error)} w-28 font-mono uppercase`}
            />
          </label>
        </div>
        <p id="color-ayuda" className={ayuda}>
          Va en la cabecera y en el botón de tus mails, y en el reporte.{claro ? " Es un color claro: lo que va arriba de él se escribe en negro." : ""}
        </p>
        <TextoDeError id="color-error" texto={error} />
      </fieldset>
    </div>
  );
}

function PalabrasQueNoUsa({ palabras, error, onCambio }: { palabras: string[]; error?: string; onCambio: (p: string[]) => void }) {
  const [nueva, setNueva] = useState("");
  const [problema, setProblema] = useState("");
  const agregar = () => {
    const p = problemaDeLaPalabra(nueva, palabras);
    if (p) return setProblema(p);
    onCambio([...palabras, nueva.trim().replace(/\s+/g, " ")]);
    setNueva("");
    setProblema("");
  };
  // Enter agrega la palabra; no guarda toda la pantalla
  const enter = (e: KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    agregar();
  };
  return (
    <section className={seccion} aria-labelledby="palabras-titulo">
      <h3 id="palabras-titulo" className="text-sm font-semibold text-slate-900">
        Palabras que tu marca no usa
      </h3>
      {palabras.length > 0 ? (
        <ul className="flex flex-wrap gap-2" data-testid="palabras">
          {palabras.map((p) => (
            <li key={p} className="inline-flex items-center rounded-full bg-slate-100 pl-3.5 text-sm font-medium text-slate-800">
              {p}
              <button
                type="button"
                aria-label={`Sacar «${p}»`}
                onClick={() => onCambio(palabras.filter((x) => x !== p))}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full text-slate-500 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
              >
                <X size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className={ayuda}>Ninguna cargada.</p>
      )}
      <div className="flex gap-2">
        <label className="min-w-0 flex-1">
          <span className="sr-only">Nueva palabra</span>
          <input
            value={nueva}
            maxLength={PALABRA_MAX}
            onChange={(e) => {
              setNueva(e.target.value);
              setProblema("");
            }}
            onKeyDown={enter}
            placeholder="Agregar palabra"
            aria-invalid={!!problema || undefined}
            className={campoClase(!!problema)}
          />
        </label>
        <button type="button" className={btnSecundario} onClick={agregar}>
          Agregar
        </button>
      </div>
      <TextoDeError texto={problema || error} />
      <p className={ayuda}>En Mensajes, un texto con una de estas palabras no queda listo hasta que la saques.</p>
    </section>
  );
}

const PESTANAS = [
  { id: "mail", etiqueta: "Email" },
  { id: "whatsapp", etiqueta: "WhatsApp" },
  { id: "reporte", etiqueta: "Reporte" },
] as const;
type Pestana = (typeof PESTANAS)[number]["id"];

function VistaPrevia({ estilo, f, datos }: { estilo: EstiloDeMarca; f: Formulario; datos: Branding }) {
  const [pestana, setPestana] = useState<Pestana>("mail");
  const tono = tonoDe(f.tono);
  const firma = f.firma.trim().replace(/\s+/g, " ");
  const web = webParaMostrar(f.web);
  const vetadas = palabrasEn(`${tono.asunto} ${tono.cuerpo}`, f.palabras);
  // flechas para moverse entre las pestañas, como en cualquier lista de pestañas
  const flechas = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const i = PESTANAS.findIndex((p) => p.id === pestana);
    const otra = PESTANAS[(i + (e.key === "ArrowRight" ? 1 : PESTANAS.length - 1)) % PESTANAS.length].id;
    setPestana(otra);
    document.getElementById(`pestana-${otra}`)?.focus();
  };
  return (
    <section aria-label="Vista previa" className="min-w-0 flex-[1_1_420px] space-y-4 rounded-2xl border border-slate-200 bg-white p-5 lg:sticky lg:top-4">
      <div role="tablist" aria-label="Vista previa" className="flex gap-6 border-b border-slate-100">
        {PESTANAS.map((p) => (
          <button
            key={p.id}
            id={`pestana-${p.id}`}
            type="button"
            role="tab"
            aria-selected={pestana === p.id}
            aria-controls={`panel-${p.id}`}
            tabIndex={pestana === p.id ? 0 : -1}
            onClick={() => setPestana(p.id)}
            onKeyDown={flechas}
            className={`-mb-px min-h-[44px] border-b-2 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${pestana === p.id ? "border-violet-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
          >
            {p.etiqueta}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`panel-${pestana}`} aria-labelledby={`pestana-${pestana}`} className="space-y-3">
        {pestana === "mail" && (
          <VistaMail
            estilo={estilo}
            asunto={tono.asunto}
            cuerpo={tono.cuerpo}
            codigo={CODIGO_DE_EJEMPLO}
            firma={firma}
            extra={<p className="text-sm text-slate-500">{web ? `El botón lleva a ${web}.` : "Cargá tu web para que el botón lleve a tu tienda."}</p>}
          />
        )}
        {pestana === "whatsapp" && <VistaWhatsApp estilo={estilo} cuerpo={tono.cuerpo} codigo={CODIGO_DE_EJEMPLO} firma={firma} />}
        {pestana === "reporte" && <VistaReporte estilo={estilo} campana={datos.ultimaCampana} />}
        {pestana !== "reporte" && vetadas.length > 0 && (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="vetadas-en-ejemplo">
            Este ejemplo dice «{vetadas.join("», «")}», que tu marca no usa. En Mensajes te avisamos antes de que salga.
          </p>
        )}
      </div>
    </section>
  );
}

function FormularioBranding({ inicial }: { inicial: Branding }) {
  const [datos, setDatos] = useState(inicial);
  const [f, setF] = useState<Formulario>(() => formularioDe(inicial));
  const [errores, setErrores] = useState<Partial<Record<Campo, string>>>({});
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [listo, setListo] = useState(false);
  const cambiar = (c: Partial<Formulario>) => {
    setF((x) => ({ ...x, ...c }));
    setListo(false);
  };
  const estilo: EstiloDeMarca = { color: esColor(f.color) ? f.color : datos.color, letra: f.letra, logo: f.logo, marca: datos.marca };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setListo(false);
    const v = validarFormulario(f);
    if (!v.ok) return setErrores(v.errores);
    setErrores({});
    setGuardando(true);
    const l = await llamar<Branding>(() => brandingApi.guardar(v.identidad));
    setGuardando(false);
    if (l.ok) {
      setDatos(l.data);
      setF(formularioDe(l.data));
      setListo(true);
      return;
    }
    const campo = typeof l.extra.campo === "string" ? (l.extra.campo as Campo) : null;
    if (campo && CAMPOS.includes(campo)) setErrores({ [campo]: textoError(l) });
    else setError(textoError(l));
  };

  return (
    <form onSubmit={guardar} noValidate className="space-y-5">
      {!datos.guardado &&
        (datos.propuesta ? (
          <p className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900" data-testid="propuesta">
            Lo cargamos con la voz que ya usan tus campañas por QR: el tono, la firma y las palabras. Revisalo y guardá.
          </p>
        ) : (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="sin-guardar">
            Todavía no guardaste el branding de esta marca. Hasta que lo guardes, el reporte sale con el diseño de DEENEX y las campañas por QR siguen con la voz de antes.
          </p>
        ))}

      <div className="flex flex-wrap items-start gap-5">
        <section aria-label="Tu marca" className="min-w-0 flex-[1_1_380px] rounded-2xl border border-slate-200 bg-white px-5 py-1">
          <LogoDeLaMarca
            estilo={estilo}
            bytes={f.logoCambiado ? null : datos.logoBytes}
            error={errores.logo}
            onCambio={(logo) => {
              cambiar({ logo, logoCambiado: true });
              setErrores((x) => ({ ...x, logo: undefined }));
            }}
          />

          <div className={seccion}>
            <label htmlFor="web" className="block text-sm font-semibold text-slate-900">
              Tu web
            </label>
            <input
              id="web"
              type="url"
              inputMode="url"
              value={f.web}
              onChange={(e) => cambiar({ web: e.target.value })}
              placeholder="https://"
              aria-invalid={!!errores.web || undefined}
              aria-describedby={errores.web ? "web-error" : undefined}
              className={campoClase(!!errores.web)}
            />
            <TextoDeError id="web-error" texto={errores.web} />
          </div>

          <ColorDeLaMarca color={f.color} error={errores.color} onCambio={(color) => cambiar({ color })} />

          <div className={seccion}>
            <Opciones<Letra>
              nombre="letra"
              leyenda="Letra"
              opciones={LETRAS.map((l) => l.id)}
              valor={f.letra}
              onCambio={(letra) => cambiar({ letra })}
              etiqueta={(o) => {
                const l = LETRAS.find((x) => x.id === o) ?? LETRAS[0];
                return <span style={{ fontFamily: l.css }}>{l.etiqueta}</span>;
              }}
            />
          </div>

          <div className={seccion}>
            <Opciones<TonoMarca>
              nombre="tono"
              leyenda="Cómo habla tu marca"
              opciones={TONOS.map((t) => t.id)}
              valor={f.tono}
              onCambio={(tono) => cambiar({ tono })}
              etiqueta={(o) => tonoDe(o).etiqueta}
              ayudaId="tono-ayuda"
            />
            <p id="tono-ayuda" className={ayuda}>
              Es la voz de la marca: cada campaña arranca con este tono, esta firma y estas palabras.
            </p>
          </div>

          <PalabrasQueNoUsa palabras={f.palabras} error={errores.palabras} onCambio={(palabras) => cambiar({ palabras })} />

          <div className={seccion}>
            <label htmlFor="firma" className="block text-sm font-semibold text-slate-900">
              Firma
            </label>
            <input
              id="firma"
              value={f.firma}
              maxLength={FIRMA_MAX}
              onChange={(e) => cambiar({ firma: e.target.value })}
              placeholder={`Ej: Equipo ${nombreParaMostrar(datos.marca)}`}
              aria-invalid={!!errores.firma || undefined}
              aria-describedby={errores.firma ? "firma-ayuda firma-error" : "firma-ayuda"}
              className={campoClase(!!errores.firma)}
            />
            <p id="firma-ayuda" className={ayuda}>
              Cierra cada mensaje. Vacía, los mensajes salen sin firma.
            </p>
            <TextoDeError id="firma-error" texto={errores.firma} />
          </div>
        </section>

        <VistaPrevia estilo={estilo} f={f} datos={datos} />
      </div>

      {error && (
        <p role="alert" className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}
      <div className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 flex flex-wrap items-center gap-3 border-t border-slate-200 bg-white/95 px-4 sm:px-6 py-3 backdrop-blur" data-testid="barra-guardar">
        <button type="submit" className={btnPrimario} disabled={guardando}>
          {guardando && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
          Guardar
        </button>
        {listo && (
          <p role="status" className="text-sm text-emerald-700">
            Branding guardado.
          </p>
        )}
        {!listo && datos.actualizado && (
          <p className="text-sm text-slate-500">
            Guardado {datos.actualizado.autor?.nombre ? `por ${datos.actualizado.autor.nombre} ` : ""}el {diaDe(datos.actualizado.at)}
          </p>
        )}
      </div>
    </form>
  );
}

export default function BrandingPage() {
  const pedido = usePedido<Branding>(() => brandingApi.ver(), "branding");
  const d = pedido.dato;
  return (
    <Pagina>
      <EncabezadoPagina antetitulo="Tu marca" titulo="Branding" bajada="Tus mails, tus WhatsApp y tus reportes salen con tu marca, no con la nuestra." />
      {pedido.error && !d && <ErrorTarjeta texto={pedido.error} onReintentar={pedido.recargar} />}
      {pedido.cargando && !d && <Esqueleto alto="h-96" etiqueta="Cargando el branding" />}
      {d && <FormularioBranding inicial={d} />}
    </Pagina>
  );
}
